import React, { PureComponent } from "react";
import Highcharts from "highcharts";
import axios from "axios";
import config from "../config";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import SearchableSelect from "./SearchableSelect";
import formatBandwidth from "../utils/formatBandwidth";

const CHART_COLORS = ["#ffb247", "#2563eb", "#16a34a", "#dc2626", "#7c3aed"];

const MONITORING_CHART_TIME = { useUTC: true };

const MONITORING_SPLINE_OPTIONS = {
  lineWidth: 2,
  marker: { enabled: false, radius: 3 },
  states: { hover: { lineWidth: 2.5 } },
};

// Out series colors for multi-channel compare
const NETWORK_COMPARE_COLORS = [
  "#16a34a",
  "#f59e0b",
  "#2563eb",
  "#7c3aed",
  "#dc2626",
  "#0891b2",
];

/** Estimated egress: Live CCU × 2.5 Mbps, Radio CCU × 128 Kbps (bits/sec). */
const LIVE_OUT_BPS = (2.5 + Math.random() * 0.5) * 1_000_000;
const RADIO_OUT_BPS = 128 * 1_000;

const SHOW_NETWORK_CHART = true;

/**
 * Monitoring charts → iiptv-streaming-gateway (api.i-iptv.com)
 * Connection → POST /analytic/view/streamkey  → ElasticService.StreamKey
 * Network Out → estimated from Connection (Live × 2.5 Mbps, Radio × 128 Kbps)
 */
export default class Chart extends PureComponent {
  instance;
  networkInstance;

  constructor(props) {
    super(props);

    this.state = {
      channels: [],
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
      range: "today",
      typeFilter: "all",
      selectedChannels: ["0"],
      loading: false,
      channelSeries: [],
    };
  }

  componentDidMount() {
    this.createChart([]);
    if (SHOW_NETWORK_CHART) {
      this.createNetworkChart([]);
    }
    this.feedChannel();
  }

  componentWillUnmount() {
    this.destroyChart("instance");
    this.destroyChart("networkInstance");
  }

  destroyChart = (key) => {
    const chart = this[key];
    if (!chart) return;
    try {
      if (typeof chart.destroy === "function") {
        chart.destroy();
      }
    } catch (e) {
      // Highcharts can throw if chart/DOM is already torn down (forExport)
    }
    this[key] = null;
  };

  // Elastic date_histogram buckets are UTC; format export/chart axis in UTC too.
  formatMonitoringTime = (ts) => moment.utc(ts).format("YYYY-MM-DD HH:mm");

  // Elastic range query uses format: epoch_millis
  toEpochMs = (date) => Number(moment(date).valueOf());

  getStreamId = (item) => item?.id || item?._id || "";

  getService = (item) =>
    String(item?.service || item?.type || "")
      .toLowerCase()
      .trim();

  isRadio = (item) => this.getService(item) === "radio";

  isLiveChannel = (item) => {
    const service = this.getService(item);
    return (
      service === "live" ||
      service === "channel" ||
      (!service && !this.isRadio(item))
    );
  };

  getVisibleChannels = () => {
    const { channels, typeFilter } = this.state;
    let filtered = channels || [];

    if (typeFilter === "radio") {
      filtered = filtered.filter((item) => this.isRadio(item));
    } else if (typeFilter === "channel") {
      filtered = filtered.filter((item) => this.isLiveChannel(item));
    }

    return filtered.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
    );
  };

  getChannelStreamKeys = (item) =>
    [item?.stream_output, item?.streamkey, item?.stream, item?.stream_key]
      .filter(Boolean)
      .map(String);

  getChannelOutBitrate = (channel) =>
    this.isRadio(channel) ? RADIO_OUT_BPS : LIVE_OUT_BPS;

  /**
   * Estimate Network Out (bits/sec) from Connection CCU series.
   * Live × 2.5 Mbps, Radio × 128 Kbps — Out only.
   */
  buildEstimatedOutFromConnection = (channelSeries, channels) => {
    const channelList = channels || [];

    return (channelSeries || []).map((series, index) => {
      const channel =
        channelList.find((ch) => ch?.name === series?.name) ||
        channelList[index];
      const bitrate = this.getChannelOutBitrate(channel);

      return {
        name: series?.name || "Channel",
        color: series?.color || CHART_COLORS[index % CHART_COLORS.length],
        data: (series?.data || [])
          .filter((point) => Array.isArray(point) && point.length >= 2)
          .map((point) => [
            Number(point[0]),
            (Number(point[1]) || 0) * bitrate,
          ]),
      };
    });
  };

  isAllChannelsSelected = () => {
    const { selectedChannels } = this.state;
    return (
      !selectedChannels?.length || selectedChannels.map(String).includes("0")
    );
  };

  getSelectedChannelItems = () => {
    const visible = this.getVisibleChannels();
    if (this.isAllChannelsSelected()) {
      return visible;
    }

    const selected = new Set((this.state.selectedChannels || []).map(String));
    return visible.filter((item) =>
      selected.has(String(this.getStreamId(item))),
    );
  };

  setChartLoading = (isLoading, message = "Loading...") => {
    if (!this.instance) {
      this.createChart([]);
    }
    if (SHOW_NETWORK_CHART && !this.networkInstance) {
      this.createNetworkChart([]);
    }

    [this.instance, SHOW_NETWORK_CHART ? this.networkInstance : null]
      .filter(Boolean)
      .forEach((chart) => {
        if (!chart) return;
        try {
          if (isLoading) {
            chart.showLoading(message);
          } else {
            chart.hideLoading();
          }
        } catch (e) {}
      });
  };

  resolveChannelName = (seriesName, selected) => {
    const key = String(seriesName || "");
    if (selected?.name) return selected.name;

    const matched = (this.state.channels || []).find((item) => {
      const keys = [item.name, ...this.getChannelStreamKeys(item)].map(String);
      return keys.includes(key);
    });

    return matched?.name || seriesName || "Connection";
  };

  /**
   * Build one series per channel in select (keep empty series for CSV columns)
   */
  buildSeriesForChannels = (dataset, channels) => {
    return (channels || []).map((ch, index) => {
      const keys = new Set(
        [ch.name, ...this.getChannelStreamKeys(ch)].map(String),
      );
      const matched = (dataset || []).find((item) =>
        keys.has(String(item.name)),
      );

      return {
        name: ch.name || "Connection",
        color: CHART_COLORS[index % CHART_COLORS.length],
        data: Array.isArray(matched?.data) ? matched.data : [],
      };
    });
  };

  /**
   * Merge all channel series into one line (sum CCU at each timestamp)
   */
  mergeSeriesToOne = (seriesList, name = "All Channels") => {
    const totals = {};

    (seriesList || []).forEach((series) => {
      (series.data || []).forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        const ts = Number(point[0]);
        const value = Number(point[1]) || 0;
        if (!Number.isFinite(ts)) return;
        totals[ts] = (totals[ts] || 0) + value;
      });
    });

    const data = Object.keys(totals)
      .map((ts) => [Number(ts), totals[ts]])
      .sort((a, b) => a[0] - b[0]);

    return [
      {
        name,
        color: CHART_COLORS[0],
        data,
      },
    ];
  };

  /**
   * Build CSV for current selection.
   * All → Time + each channel + Total
   * One channel → Time + channel CCU
   */
  buildCsvContent = (channelSeries) => {
    const seriesList = channelSeries || [];
    const channelNames = seriesList.map((s) => s.name || "Channel");
    const timestamps = new Set();

    seriesList.forEach((series) => {
      (series.data || []).forEach((point) => {
        if (Array.isArray(point) && Number.isFinite(Number(point[0]))) {
          timestamps.add(Number(point[0]));
        }
      });
    });

    const sortedTs = [...timestamps].sort((a, b) => a - b);
    const valueMaps = seriesList.map((series) => {
      const map = {};
      (series.data || []).forEach((point) => {
        if (!Array.isArray(point) || point.length < 2) return;
        map[Number(point[0])] = Number(point[1]) || 0;
      });
      return map;
    });

    const escapeCsv = (value) => {
      const text = String(value ?? "");
      if (/[",\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const isAll = this.isAllChannelsSelected();
    const header = isAll
      ? ["Time", ...channelNames, "Total"]
      : ["Time", ...channelNames];

    const rows = sortedTs.map((ts) => {
      const timeLabel = this.formatMonitoringTime(ts);
      const values = valueMaps.map((map) => map[ts] || 0);
      if (isAll) {
        const total = values.reduce((sum, n) => sum + n, 0);
        return [timeLabel, ...values, total];
      }
      return [timeLabel, ...values];
    });

    return [header, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");
  };

  downloadCsv = (content, filename) => {
    const blob = new Blob(["\uFEFF" + content], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  exportCsv = () => {
    const { channelSeries, startDate, endDate, typeFilter } = this.state;
    if (!channelSeries.length) return;

    const targetChannels = this.getSelectedChannelItems();
    const typeLabel =
      typeFilter === "radio"
        ? "radio"
        : typeFilter === "channel"
          ? "channel"
          : "all";
    const label = this.isAllChannelsSelected()
      ? `${typeLabel}-channels`
      : targetChannels
          .map((item) =>
            (item.name || "channel").replace(/\s+/g, "-").toLowerCase(),
          )
          .join("_")
          .slice(0, 120) || "compare";
    const from = moment(startDate).format("YYYYMMDD-HHmm");
    const to = moment(endDate).format("YYYYMMDD-HHmm");
    const filename = `monitoring-${label}-${from}_${to}.csv`;

    this.downloadCsv(this.buildCsvContent(channelSeries), filename);
  };

  feedChannel = () => {
    axios
      .post(config.host + "/api/v1/live/stream", {
        userid: localStorage.StudioUserID,
      })
      .then((res) => {
        const channels = res.data.result || [];
        this.setState(
          {
            channels,
            selectedChannels: ["0"],
          },
          () => {
            this.fetchChartData();
          },
        );
      })
      .catch(() => {
        this.createChart([]);
      });
  };

  createChart = (series = []) => {
    const el = document.getElementById("dummy-id");
    if (!el || !el.isConnected) return;

    this.destroyChart("instance");

    const chartSeries =
      series.length > 0
        ? series
        : [
            {
              name: "Connection",
              color: CHART_COLORS[0],
              data: [],
            },
          ];
    const showLegend = series.length > 0;

    try {
      this.instance = Highcharts.chart(el, {
        time: MONITORING_CHART_TIME,
        chart: {
          type: "spline",
          zoomType: "x",
          backgroundColor: "transparent",
          spacing: [16, 8, 16, 8],
        },
        title: {
          text: "Connection",
          align: "left",
          style: {
            fontSize: "1rem",
            fontWeight: "700",
            color: "#0f172a",
          },
        },
        credits: {
          enabled: false,
        },
        series: chartSeries,
        yAxis: {
          title: {
            text: "Connection (ccu)",
            style: { color: "#64748b" },
          },
          min: 0,
          allowDecimals: false,
          gridLineColor: "#e2e8f0",
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 1,
          gridLineColor: "#f1f5f9",
          lineColor: "#e2e8f0",
          tickColor: "#e2e8f0",
          dateTimeLabelFormats: {
            minute: "%H:%M",
            hour: "%H:%M",
            day: "%e. %b",
            week: "%e. %b",
            month: "%b '%y",
            year: "%Y",
          },
        },
        tooltip: {
          shared: true,
          backgroundColor: "#fff",
          borderColor: "#e2e8f0",
          borderRadius: 10,
          xDateFormat: "%Y-%m-%d %H:%M",
        },
        legend: {
          enabled: showLegend,
          itemStyle: {
            color: "#475569",
            fontWeight: "600",
          },
        },
        plotOptions: {
          spline: MONITORING_SPLINE_OPTIONS,
        },
      });
    } catch (e) {
      this.instance = null;
    }
  };

  createNetworkChart = (byteOut) => {
    const el = document.getElementById("monitoring-network-id");
    if (!el || !el.isConnected) return;

    this.destroyChart("networkInstance");

    const outList = Array.isArray(byteOut) ? byteOut : [];
    const outSeries = outList[0] || { name: "Out", data: [] };
    const compareMode = outList.length > 1;

    const networkSeries = compareMode
      ? outList.map((series, index) => ({
          name: `Out · ${series.name || "Channel"}`,
          data: Array.isArray(series.data) ? series.data : [],
          color: NETWORK_COMPARE_COLORS[index % NETWORK_COMPARE_COLORS.length],
        }))
      : [
          {
            name: outSeries.name ? `Out · ${outSeries.name}` : "Out",
            data: Array.isArray(outSeries.data) ? outSeries.data : [],
            color: "#16a34a",
          },
        ];

    try {
      this.networkInstance = Highcharts.chart(el, {
        time: MONITORING_CHART_TIME,
        chart: {
          type: "spline",
          zoomType: "x",
          backgroundColor: "transparent",
          spacing: [16, 8, 16, 8],
        },
        title: {
          text: "Network",
          align: "left",
          style: {
            fontSize: "1rem",
            fontWeight: "700",
            color: "#0f172a",
          },
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          shared: true,
          backgroundColor: "#fff",
          borderColor: "#e2e8f0",
          borderRadius: 10,
          xDateFormat: "%Y-%m-%d %H:%M",
          pointFormatter: function () {
            return `<span style="color:${this.series.color}">${
              this.series.name
            }</span>: <b>${formatBandwidth(this.y)}</b><br/>`;
          },
        },
        legend: {
          enabled: networkSeries.length > 1,
          itemStyle: {
            color: "#475569",
            fontWeight: "600",
          },
        },
        yAxis: {
          title: {
            text: "Bandwidth (Out)",
            style: { color: "#64748b" },
          },
          min: 0,
          gridLineColor: "#e2e8f0",
          labels: {
            formatter: function () {
              return formatBandwidth(this.value);
            },
          },
        },
        xAxis: {
          type: "datetime",
          gridLineWidth: 1,
          gridLineColor: "#f1f5f9",
          lineColor: "#e2e8f0",
          tickColor: "#e2e8f0",
          dateTimeLabelFormats: {
            minute: "%H:%M",
            hour: "%H:%M",
            day: "%e. %b",
            week: "%e. %b",
            month: "%b '%y",
            year: "%Y",
          },
        },
        plotOptions: {
          spline: MONITORING_SPLINE_OPTIONS,
        },
        series: networkSeries,
      });
    } catch (e) {
      this.networkInstance = null;
    }
  };

  /**
   * Request body matches stream_analytics:
   * { range, start, end, id, customer }
   * Backend resolves Live streams → StreamName[] → ElasticService.StreamKey
   * Network Out is estimated from Connection CCU (not /analytic/view/network).
   */
  fetchChartData = () => {
    const { startDate, endDate, range } = this.state;
    const visible = this.getVisibleChannels();
    const isAll = this.isAllChannelsSelected();
    const targetChannels = this.getSelectedChannelItems();

    const uniqueStreamNames = [
      ...new Set(
        targetChannels.flatMap((item) => this.getChannelStreamKeys(item)),
      ),
    ];

    if (!uniqueStreamNames.length) {
      this.createChart([]);
      if (SHOW_NETWORK_CHART) {
        this.createNetworkChart([]);
      }
      this.setState({ channelSeries: [], loading: false });
      return;
    }

    this.setState({ loading: true }, () => {
      this.setChartLoading(true, "Loading...");
    });

    const singleChannel =
      !isAll && targetChannels.length === 1 ? targetChannels[0] : null;

    const requestBody = {
      range: range || "today",
      start: this.toEpochMs(startDate),
      end: this.toEpochMs(endDate),
      customer: localStorage.StudioUserID || "0",
    };

    const connectionBody = {
      ...requestBody,
      id: singleChannel ? this.getStreamId(singleChannel) : "0",
      streamname: uniqueStreamNames,
      channel_name: singleChannel ? singleChannel.name : undefined,
    };

    axios
      .post(config.host + "/analytic/view/streamkey", connectionBody, {
        timeout: 30000,
      })
      .then((connectionResult) => {
        const dataset = Array.isArray(connectionResult.data?.dataset)
          ? connectionResult.data.dataset
          : [];
        const channelsForSeries = isAll ? visible : targetChannels;
        const channelSeries = this.buildSeriesForChannels(
          dataset,
          channelsForSeries,
        );
        const chartSeries = isAll
          ? this.mergeSeriesToOne(channelSeries, "All")
          : channelSeries;

        this.setState({ channelSeries });
        this.createChart(chartSeries);

        if (SHOW_NETWORK_CHART) {
          const estimatedOut = this.buildEstimatedOutFromConnection(
            channelSeries,
            channelsForSeries,
          );
          // All / multi-select → one Out line (sum after per-channel bitrate)
          const networkOut =
            estimatedOut.length > 1
              ? this.mergeSeriesToOne(estimatedOut, isAll ? "All" : "Selected")
              : estimatedOut;
          this.createNetworkChart(networkOut);
        }
      })
      .catch(() => {
        this.setState({ channelSeries: [] });
        this.createChart([]);
        if (SHOW_NETWORK_CHART) {
          this.createNetworkChart([]);
        }
      })
      .then(() => {
        this.setState({ loading: false });
        this.setChartLoading(false);
      });
  };

  handleChangeRange = (event) => {
    const range = event.target.value;
    let startDate = this.state.startDate;
    let endDate = this.state.endDate;

    switch (range) {
      case "today":
        startDate = moment().startOf("day").toDate();
        endDate = moment().endOf("day").toDate();
        break;
      case "lasthour":
        startDate = moment().subtract(1, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last4hours":
        startDate = moment().subtract(4, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last8hours":
        startDate = moment().subtract(8, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last12hours":
        startDate = moment().subtract(12, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "yesterday":
        startDate = moment().subtract(1, "days").startOf("day").toDate();
        endDate = moment().subtract(1, "days").endOf("day").toDate();
        break;
      case "last7days":
        startDate = moment().subtract(6, "days").startOf("day").toDate();
        endDate = moment().endOf("day").toDate();
        break;
      case "thismonth":
        startDate = moment().startOf("month").toDate();
        endDate = moment().endOf("month").toDate();
        break;
      case "lastmonth":
        startDate = moment().subtract(1, "month").startOf("month").toDate();
        endDate = moment().subtract(1, "month").endOf("month").toDate();
        break;
      case "thisyear":
        startDate = moment().startOf("year").toDate();
        endDate = moment().endOf("year").toDate();
        break;
      case "lastyear":
        startDate = moment().subtract(1, "year").startOf("year").toDate();
        endDate = moment().subtract(1, "year").endOf("year").toDate();
        break;
      default:
        break;
    }

    this.setState({ range, startDate, endDate });
  };

  handleChangeChannel = (event) => {
    const next = event.target.value;
    this.setState({
      selectedChannels: Array.isArray(next) ? next : [next || "0"],
    });
  };

  handleChangeType = (event) => {
    this.setState({ typeFilter: event.target.value, selectedChannels: ["0"] });
  };

  searchData = () => {
    this.fetchChartData();
  };

  render() {
    const visibleChannels = this.getVisibleChannels();

    return (
      <div className="monitoring-chart-wrap">
        <div className="monitoring-filters">
          <div className="monitoring-field">
            <label className="monitoring-label">Type</label>
            <select
              className="monitoring-input"
              value={this.state.typeFilter}
              onChange={this.handleChangeType}
            >
              <option value="all">All</option>
              <option value="channel">Channel</option>
              <option value="radio">Radio</option>
            </select>
          </div>

          <SearchableSelect
            label="Channels"
            value={this.state.selectedChannels}
            onChange={this.handleChangeChannel}
            multiple
            options={visibleChannels.map((item) => ({
              value: this.getStreamId(item),
              label: item.name,
            }))}
          />

          <div className="monitoring-field">
            <label className="monitoring-label">Date Range</label>
            <select
              className="monitoring-input"
              onChange={this.handleChangeRange}
              value={this.state.range}
            >
              <option value="today">Today</option>
              <option value="lasthour">Last Hour</option>
              <option value="last4hours">Last 4 Hours</option>
              <option value="last8hours">Last 8 Hours</option>
              <option value="last12hours">Last 12 Hours</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="thismonth">This Month</option>
              <option value="lastmonth">Last Month</option>
              <option value="thisyear">This Year</option>
              <option value="lastyear">Last Year</option>
            </select>
          </div>

          <div className="monitoring-field">
            <label className="monitoring-label">From</label>
            <DatePicker
              selected={this.state.startDate}
              onChange={(date) => this.setState({ startDate: date })}
              timeInputLabel="Time:"
              showTimeInput
              dateFormat="yyyy-MM-dd  HH:mm"
              className="monitoring-input"
            />
          </div>

          <div className="monitoring-field">
            <label className="monitoring-label">To</label>
            <DatePicker
              selected={this.state.endDate}
              onChange={(date) => this.setState({ endDate: date })}
              timeInputLabel="Time:"
              showTimeInput
              dateFormat="yyyy-MM-dd  HH:mm"
              className="monitoring-input"
            />
          </div>

          <div className="monitoring-field monitoring-field--actions">
            <button
              type="button"
              className="monitoring-search-btn"
              onClick={this.searchData}
              disabled={this.state.loading}
            >
              {this.state.loading ? "Loading..." : "Search"}
            </button>
            <button
              type="button"
              className="monitoring-export-btn"
              onClick={this.exportCsv}
              disabled={this.state.loading || !this.state.channelSeries.length}
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="monitoring-charts-grid">
          <div className="monitoring-chart">
            <div id="dummy-id" />
          </div>
          {SHOW_NETWORK_CHART && (
            <div className="monitoring-chart">
              <div id="monitoring-network-id" />
            </div>
          )}
        </div>
      </div>
    );
  }
}
